import { useQueryClient } from "@tanstack/react-query";
import { configuration } from "src/configuration";
import {
  EditTaskItemTeamDto,
  FetchError,
  GetUserTaskItemsDto,
  optimisticallyUpdateQueryData,
  queryKeys,
  TaskItemDetailDto,
  useAccessToken,
  useTaskItemMutation,
} from "src/http";

type Variables = {
  taskItemGuid: string;
  oldTeamSlug?: string;
  newTeam: {
    name: string;
    slug: string;
  } | null;
  ownerUserGuids: string[];
};

const useEditTaskItemTeamRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function editTaskItemTeamRequest(variables: Variables) {
    const accessToken = await getAccessToken();
    const body: EditTaskItemTeamDto = {
      teamSlug: variables.newTeam?.slug ?? null,
    };

    const response = await fetch(`${configuration.apiRootUrl}/task-items/${variables.taskItemGuid}/team`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "put",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { editTaskItemTeamRequest };
};

const useEditTaskItemTeam = () => {
  const { editTaskItemTeamRequest } = useEditTaskItemTeamRequest();
  const queryClient = useQueryClient();

  return useTaskItemMutation({
    mutationFn: editTaskItemTeamRequest,
    onMutate: async (variables) => {
      let cachedDetailData: TaskItemDetailDto | undefined;
      let cachedUserData: { [userGuid: string]: GetUserTaskItemsDto | undefined } = {};

      cachedDetailData = await optimisticallyUpdateQueryData<TaskItemDetailDto>(
        queryClient,
        queryKeys.taskItems.getTaskItemDetail(variables.taskItemGuid),
        () => ({
          team: variables.newTeam
            ? {
                name: variables.newTeam?.name,
                slug: variables.newTeam?.slug,
              }
            : undefined,
        })
      );

      if (variables.ownerUserGuids) {
        variables.ownerUserGuids.forEach(async (userGuid) => {
          cachedUserData[userGuid] = await optimisticallyUpdateQueryData<GetUserTaskItemsDto>(
            queryClient,
            queryKeys.taskItems.getUserTaskItems(userGuid),
            (cachedData) => ({
              taskItems: cachedData.taskItems.map((taskItem) => {
                if (taskItem.guid === variables.taskItemGuid) {
                  return {
                    ...taskItem,
                    team: variables.newTeam
                      ? {
                          name: variables.newTeam?.name,
                          slug: variables.newTeam?.slug,
                        }
                      : undefined,
                  };
                } else {
                  return taskItem;
                }
              }),
            })
          );
        });
      }

      return {
        cachedDetailData,
        cachedUserData,
      };
    },
  });
};

export { useEditTaskItemTeam };
